import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { api, DocumentChunk } from "../api";

// ── Types ─────────────────────────────────────────────────────────────────
interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  displayedContent: string;
  isStreaming: boolean;
  sources?: DocumentChunk[];
  knowledgeSource?: "documents" | "ai_knowledge" | "web_search";
  error?: boolean;
}

interface Props {
  /** Controlled from App: which conversation is open (undefined = new chat) */
  conversationId?: string;
  /** Called when a chat reply comes back with a (possibly new) conversation id */
  onConversationChange: (id: string) => void;
}

const SUGGESTED = [
  "What is the leave policy?",
  "How do I submit an expense claim?",
  "What is the onboarding process for new employees?",
  "Who do I contact for IT support?",
];

const uid = () => Math.random().toString(36).slice(2);
const WORDS_PER_TICK = 3;
const TICK_MS = 35;

function confidenceBadge(score: number) {
  if (score >= 0.85) return { label: "High match",    color: "#16a34a" };
  if (score >= 0.6)  return { label: "Good match",    color: "#d97706" };
  return               { label: "Partial match", color: "#9ca3af" };
}

// ── Component ──────────────────────────────────────────────────────────────
export default function ChatPanel({ conversationId, onConversationChange }: Props) {
  const [messages, setMessages]     = useState<Message[]>([]);
  const [query, setQuery]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [copied, setCopied]         = useState<string | null>(null);

  const bottomRef   = useRef<HTMLDivElement>(null);
  const inputRef    = useRef<HTMLInputElement>(null);
  const abortRef    = useRef<AbortController | null>(null);

  // ── Animation state stored entirely in refs — no render dependency ──────
  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef   = useRef<{ id: string; words: string[]; idx: number } | null>(null);
  const msgCountRef = useRef(0);

  // ── Track last loaded conversation so we only reload when it changes ────
  const loadedConvRef = useRef<string | undefined>(undefined);

  // Auto-scroll whenever messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Load conversation history when selectedConversationId changes ────────
  useEffect(() => {
    // undefined → new chat: clear messages
    if (!conversationId) {
      if (loadedConvRef.current !== undefined) {
        setMessages([]);
        msgCountRef.current = 0;
        loadedConvRef.current = undefined;
      }
      return;
    }

    // Same conversation already loaded — skip
    if (conversationId === loadedConvRef.current) return;

    loadedConvRef.current = conversationId;
    setLoadingHistory(true);

    api.getConversation(conversationId)
      .then(detail => {
        const loaded: Message[] = detail.messages.map(m => ({
          id: String(m.id),
          role: m.role,
          content: m.content,
          displayedContent: m.content,   // no animation for history
          isStreaming: false,
          sources: m.sources ?? [],
          knowledgeSource: m.knowledge_source as "documents" | "ai_knowledge" | "web_search" | undefined,
        }));
        setMessages(loaded);
        msgCountRef.current = loaded.length;
      })
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [conversationId]);

  // Watch for a NEW message being appended (count increases) and start the
  // interval-based animation for it. We intentionally do NOT depend on the
  // full messages array so the interval never gets cancelled mid-stream.
  useEffect(() => {
    if (messages.length <= msgCountRef.current) return;
    msgCountRef.current = messages.length;

    const newest = messages[messages.length - 1];
    if (!newest || !newest.isStreaming) return;

    // Stop any previous stream that somehow didn't finish
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const words = newest.content.split(/\s+/).filter(Boolean);
    streamRef.current = { id: newest.id, words, idx: 0 };

    timerRef.current = setInterval(() => {
      const state = streamRef.current;
      if (!state) return;

      state.idx += WORDS_PER_TICK;
      const done      = state.idx >= state.words.length;
      const displayed = state.words.slice(0, state.idx).join(" ");

      setMessages(prev =>
        prev.map(m =>
          m.id === state.id
            ? { ...m, displayedContent: done ? m.content : displayed, isStreaming: !done }
            : m
        )
      );

      if (done) {
        clearInterval(timerRef.current!);
        timerRef.current  = null;
        streamRef.current = null;
      }
    }, TICK_MS);

  }, [messages.length]); // ← only fires when a new message is ADDED

  // Cleanup interval on unmount
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // ── Cancel in-flight request ────────────────────────────────────────────
  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // ── Send query ──────────────────────────────────────────────────────────
  const sendQuery = useCallback(async (text: string) => {
    if (!text.trim() || loading) return;
    const trimmed = text.trim();

    setMessages(prev => [
      ...prev,
      { id: uid(), role: "user", content: trimmed, displayedContent: trimmed, isStreaming: false },
    ]);
    setQuery("");
    setLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await api.chat(trimmed, conversationId, controller.signal);

      onConversationChange(res.conversation_id);

      setMessages(prev => [
        ...prev,
        {
          id: uid(),
          role: "assistant",
          content: res.answer,
          displayedContent: "",
          isStreaming: true,
          sources: res.sources,
          knowledgeSource: res.knowledge_source,
        },
      ]);
    } catch (err: unknown) {
      // User cancelled — clear the loading state silently (no error bubble)
      const name = (err as { name?: string })?.name;
      const code = (err as { code?: string })?.code;
      if (name === "CanceledError" || name === "AbortError" || code === "ERR_CANCELED") {
        return;
      }
      setMessages(prev => [
        ...prev,
        {
          id: uid(),
          role: "assistant",
          content: "Something went wrong. Please try again.",
          displayedContent: "Something went wrong. Please try again.",
          isStreaming: false,
          error: true,
        },
      ]);
    } finally {
      abortRef.current = null;
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, conversationId, onConversationChange]);

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); sendQuery(query); };

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="chat-panel">
      <div className="messages">

        {!loadingHistory && messages.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h2>Ask anything about your company documents</h2>
            <p>Answers come from indexed internal documents — when a topic isn't covered, the AI will answer from its own knowledge and tell you so.</p>
            <div className="suggestions">
              {SUGGESTED.map(q => (
                <button key={q} className="suggestion-chip" onClick={() => sendQuery(q)}>{q}</button>
              ))}
            </div>
          </div>
        )}

        {loadingHistory && (
          <div className="empty-state">
            <div className="empty-icon" style={{ fontSize: "1.5rem" }}>⏳</div>
            <p>Loading conversation…</p>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`message-row ${msg.role}`}>
            <div className={`avatar ${msg.role}`}>{msg.role === "assistant" ? "AI" : "You"}</div>

            <div className="message-body">
              <div className={`bubble ${msg.role} ${msg.error ? "error" : ""}`}>
                {msg.role === "assistant" ? (
                  <div className="markdown-body">
                    <ReactMarkdown>{msg.displayedContent || " "}</ReactMarkdown>
                    {msg.isStreaming && <span className="cursor" />}
                  </div>
                ) : (
                  <p>{msg.content}</p>
                )}

                {msg.role === "assistant" && !msg.isStreaming && !msg.error && (
                  <button className="copy-btn" onClick={() => handleCopy(msg.id, msg.content)}>
                    {copied === msg.id ? "✓ Copied" : "Copy"}
                  </button>
                )}
              </div>

              {/* Knowledge source badge */}
              {msg.role === "assistant" && !msg.isStreaming && !msg.error && msg.knowledgeSource && (
                <div className={`knowledge-badge ${msg.knowledgeSource}`}>
                  {msg.knowledgeSource === "documents"
                    ? <><span>📄</span> From company documents</>
                    : msg.knowledgeSource === "web_search"
                    ? <><span>🌐</span> From web search</>
                    : <><span>🧠</span> From AI knowledge</>
                  }
                </div>
              )}

              {msg.sources && msg.sources.length > 0 && !msg.isStreaming && (
                <div className="sources-section">
                  <span className="sources-label">📄 Sources ({msg.sources.length})</span>
                  <div className="source-cards">
                    {msg.sources.map((src, i) => {
                      const badge = confidenceBadge(src.score);
                      return (
                        <div className="source-card" key={i}>
                          <div className="source-card-top">
                            <span className="source-icon">📑</span>
                            <span className="source-file">{src.source_file}</span>
                            <span className="source-badge" style={{ color: badge.color, borderColor: badge.color }}>
                              {badge.label}
                            </span>
                          </div>
                          <div className="source-card-bottom">
                            <span className="source-page">Page {src.page_number}</span>
                            <span className="source-excerpt">
                              {src.content.slice(0, 120)}{src.content.length > 120 ? "…" : ""}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="message-row assistant">
            <div className="avatar assistant">AI</div>
            <div className="message-body">
              <div className="bubble assistant thinking">
                <span className="dot" /><span className="dot" /><span className="dot" />
                <span className="thinking-label">Searching documents…</span>
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <form className="input-bar" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Ask a question about company documents…"
          disabled={loading || loadingHistory}
          autoFocus
        />
        {loading ? (
          <button type="button" onClick={handleStop} className="stop-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="4" width="16" height="16" rx="2" />
            </svg>
            Stop
          </button>
        ) : (
          <button type="submit" disabled={loadingHistory || !query.trim()} className="send-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            Send
          </button>
        )}
      </form>
    </div>
  );
}
